import { Crown, Clock, Percent, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface PremiumEventBadgesProps {
  isPremiumIncluded?: boolean;
  premiumDiscountPercent?: number;
  earlyAccessHours?: number;
  isCowgirlsHosted?: boolean;
  isPremiumUser?: boolean;
  compact?: boolean;
  className?: string;
}

export const PremiumEventBadges = ({
  isPremiumIncluded,
  premiumDiscountPercent,
  earlyAccessHours,
  isCowgirlsHosted,
  isPremiumUser,
  compact = false,
  className,
}: PremiumEventBadgesProps) => {
  const hasPremiumBenefits = isPremiumIncluded || (premiumDiscountPercent && premiumDiscountPercent > 0) || (earlyAccessHours && earlyAccessHours > 0);

  if (!hasPremiumBenefits && !isCowgirlsHosted) return null;

  if (compact) {
    return (
      <div className={cn('flex flex-wrap gap-1', className)}>
        {isCowgirlsHosted && (
          <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px] px-1.5 py-0">
            <Sparkles className="w-2.5 h-2.5 mr-0.5" />
            WC
          </Badge>
        )}
        {isPremiumIncluded && (
          <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-[10px] px-1.5 py-0">
            <Crown className="w-2.5 h-2.5 mr-0.5" />
            Free
          </Badge>
        )}
        {!isPremiumIncluded && premiumDiscountPercent != null && premiumDiscountPercent > 0 && (
          <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-[10px] px-1.5 py-0">
            -{premiumDiscountPercent}%
          </Badge>
        )}
        {earlyAccessHours != null && earlyAccessHours > 0 && (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-[10px] px-1.5 py-0">
            <Clock className="w-2.5 h-2.5 mr-0.5" />
            Early
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {isCowgirlsHosted && (
        <div className="flex items-center gap-2 text-sm">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
          </div>
          <span className="text-foreground font-medium">Wellness Cowgirls Event</span>
        </div>
      )}
      
      {isPremiumIncluded && (
        <div className="flex items-center gap-2 text-sm">
          <div className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Crown className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <span className={cn(
              'font-medium',
              isPremiumUser ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
            )}>
              {isPremiumUser ? 'Included with your Premium' : 'Free for Premium Members'}
            </span>
            {!isPremiumUser && (
              <p className="text-xs text-muted-foreground">Upgrade to attend for free</p>
            )}
          </div>
        </div>
      )}

      {!isPremiumIncluded && premiumDiscountPercent != null && premiumDiscountPercent > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <Percent className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <span className={cn(
              'font-medium',
              isPremiumUser ? 'text-green-600 dark:text-green-400' : 'text-foreground'
            )}>
              {isPremiumUser 
                ? `You save ${premiumDiscountPercent}%` 
                : `${premiumDiscountPercent}% off for Premium`}
            </span>
            {!isPremiumUser && (
              <p className="text-xs text-muted-foreground">Upgrade to save on this event</p>
            )}
          </div>
        </div>
      )}

      {earlyAccessHours != null && earlyAccessHours > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Clock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <span className={cn(
              'font-medium',
              isPremiumUser ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'
            )}>
              {isPremiumUser 
                ? `${earlyAccessHours}h early access` 
                : `${earlyAccessHours}h early access for Premium`}
            </span>
            {!isPremiumUser && (
              <p className="text-xs text-muted-foreground">Premium members can RSVP first</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper to check if RSVP is available based on early access
export const canRSVPWithEarlyAccess = (
  eventDate: string | Date,
  earlyAccessHours: number,
  isPremiumUser: boolean
): { canRSVP: boolean; reason?: string; premiumCanRSVP: boolean } => {
  const now = new Date();
  const eventTime = new Date(eventDate);
  
  // Calculate when general RSVP opens (event time minus any buffer you want)
  // For simplicity, we'll say general RSVP is always open unless early access is in effect
  
  if (!earlyAccessHours || earlyAccessHours <= 0) {
    return { canRSVP: true, premiumCanRSVP: true };
  }

  // Calculate when premium RSVP opens vs general
  const premiumOpenTime = new Date(eventTime.getTime() - (earlyAccessHours * 60 * 60 * 1000));
  
  // For now, we'll just indicate if premium has early access advantage
  // In practice, you'd integrate this with your RSVP limit/slot system
  
  if (isPremiumUser) {
    return { canRSVP: true, premiumCanRSVP: true };
  }

  // Check if we're still in the premium-only early access window
  // This is a simplified example - adjust based on your actual RSVP logic
  return { 
    canRSVP: true, 
    premiumCanRSVP: true,
    reason: `Premium members got ${earlyAccessHours}h head start`
  };
};
